/**
 * Login Page with Firebase Google Authentication
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogIn, Loader2 } from 'lucide-react';
import { useAuth } from '../providers/AuthProvider';
import { useApp } from '../providers/AppProvider';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, loading: authLoading, isAuthenticated } = useAuth();
  const { state, loading: appLoading } = useApp();
  const [isLoading, setIsLoading] = useState(false);

  const redirectPath = useMemo(() => {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return from || '/app';
  }, [location.state]);

  useEffect(() => {
    if (authLoading || appLoading) {
      return;
    }

    if (!isAuthenticated || !state?.currentUser) {
      return;
    }

    navigate(redirectPath, { replace: true });
  }, [authLoading, appLoading, isAuthenticated, navigate, redirectPath, state?.currentUser]);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      await signIn();
    } catch (error) {
      console.error('Login error:', error);
      // Error already handled by signIn method
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-primary/25 to-chart-4/10 bg-clip-text text-transparent">
            Intellesphere
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">Project Management System</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl">Welcome Back</CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Sign in to access your projects and tasks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={handleGoogleLogin}
              className="w-full"
              size="lg"
              disabled={isLoading || authLoading}
            >
              {isLoading || authLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-5 w-5" />
                  Sign in with Google
                </>
              )}
            </Button>

            <div className="text-center p-4 bg-primary/10 border border-primary/40 rounded-lg">
              <p className="text-sm text-primary mb-2">
                <strong>🔐 Secure Google Sign-In Enabled</strong>
              </p>
              <p className="text-xs text-primary">
                Click the button above to sign in with your Google account.
              </p>
            </div>

            <div className="text-center text-xs sm:text-sm text-muted-foreground">
              <p>By signing in, you agree to our terms and privacy policy</p>
            </div>

            <div className="text-center">
              <Button 
                variant="link" 
                onClick={() => navigate('/')}
                className="text-sm sm:text-base"
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-xs sm:text-sm text-muted-foreground">
          <p>Need help? Contact root@intellesphere.com</p>
        </div>
      </div>
    </div>
  );
}